import { cards, characters } from '../game';
import type { AttackState, GameEvent, PlayerId, PlayerObservation, PublicCardSource } from '../game/types';
import { hasFoilFinish } from './foil';

export interface CombatExchangeModel {
  key: string;
  attack: PublicCardSource;
  attackType: 'physical' | 'energy';
  name: string;
  defender: PlayerId;
  response?: PublicCardSource;
  status: 'incoming' | 'response' | 'blocked' | 'damage' | 'choice';
}

export function pendingAttack(state: PlayerObservation | null | undefined): AttackState | undefined {
  const pending = state?.pending;
  return pending && 'attack' in pending ? pending.attack : undefined;
}

function publicCopy(state: PlayerObservation | null | undefined, player: PlayerId, instanceId?: string) {
  if (!state || !instanceId) return;
  const p = state.players[player];
  for (const [zone, copies] of Object.entries({ resolving: p.resolving, board: p.board, discard: p.discard, banished: p.banished, hand: p.hand })) {
    const copy = copies.find(c => c.id === instanceId);
    if (copy) return { copy, zone };
  }
}

export function personalitySource(state: PlayerObservation, player: PlayerId, final = false): PublicCardSource {
  const p = state.players[player], controlling = state.combat?.controlling[player];
  const ally = controlling && controlling !== 'mp' ? p.board.find(c => c.id === controlling) : undefined;
  const stack = p.mpIds ?? characters[p.character].personalityIds;
  const cardId = ally?.cardId ?? stack[Math.min(p.level - 1, stack.length - 1)];
  return { kind: final ? 'final-physical' : 'personality', player, cardId,
    foil: hasFoilFinish(cardId, ally?.foil ?? !!p.mpFoils?.[p.level - 1]),
    anchor: ally ? 'board' : 'mp', anchorId: ally?.id ?? `mp-${player}`,
    ...(ally ? { cardInstanceId: ally.id } : {}) };
}

export function sourceForAttack(state: PlayerObservation, attack: AttackState): PublicCardSource {
  if (attack.presentationSource) return { ...attack.presentationSource };
  if (attack.fromFinal || !attack.sourceCardId) return personalitySource(state, attack.attacker, !!attack.fromFinal);
  const found = publicCopy(state, attack.attacker, attack.sourceId);
  const personality = personalitySource(state, attack.attacker);
  if (!attack.sourceId && personality.cardId === attack.sourceCardId) return personality;
  return { kind: 'card', player: attack.attacker, cardId: attack.sourceCardId,
    foil: hasFoilFinish(attack.sourceCardId, found?.copy.foil ?? false),
    cardInstanceId: attack.sourceId, anchor: found?.zone === 'board' ? 'board' : 'hand', anchorId: attack.sourceId };
}

export function sourceForEvent(event: GameEvent, before: PlayerObservation | null, after: PlayerObservation): PublicCardSource | undefined {
  if (event.presentationSource) return { ...event.presentationSource };
  if (event.player === undefined || !event.cardId) return;
  const found = publicCopy(before, event.player, event.cardInstanceId) ?? publicCopy(after, event.player, event.cardInstanceId);
  if (event.type === 'power' || event.type === 'attack') {
    const p = personalitySource(before ?? after, event.player);
    if (p.cardId === event.cardId) return p;
  }
  return { kind: 'card', player: event.player, cardId: event.cardId,
    foil: hasFoilFinish(event.cardId, event.cardFoil ?? found?.copy.foil ?? false),
    cardInstanceId: event.cardInstanceId, anchor: event.type === 'activate' || found?.zone === 'board' ? 'board' : 'hand', anchorId: event.cardInstanceId };
}

export function exchangeFromState(state: PlayerObservation | null | undefined): CombatExchangeModel | null {
  const attack = pendingAttack(state);
  if (!state || !attack || state.result) return null;
  const response = state.players[attack.defender].resolving.find(c => c.id !== attack.sourceId);
  return { key: `${attack.attacker}:${attack.sourceId ?? attack.sourceCardId ?? 'final'}:${attack.sourceName}`,
    attack: sourceForAttack(state, attack), attackType: attack.attackType, name: attack.sourceName, defender: attack.defender,
    status: state.pending?.kind === 'attack' ? 'incoming' : 'choice',
    ...(response ? { response: { kind: 'card' as const, player: attack.defender, cardId: response.cardId, foil: hasFoilFinish(response.cardId, response.foil), cardInstanceId: response.id, anchor: 'hand' as const } } : {}) };
}

export function exchangeFromEvent(event: GameEvent, index: number, before: PlayerObservation | null, after: PlayerObservation): CombatExchangeModel | null {
  if (event.type !== 'attack' || event.player === undefined) return null;
  const source = sourceForEvent(event, before, after) ?? personalitySource(before ?? after, event.player, !event.cardId);
  const pending = pendingAttack(after);
  const attackType = event.attackType ?? (source.kind === 'final-physical' ? 'physical' : cards[source.cardId]?.attack ?? (pending?.attacker === event.player ? pending.attackType : 'physical'));
  return { key: `event:${after.version}:${index}`, attack: source, attackType,
    name: source.kind === 'final-physical' ? 'Final Physical Attack' : cards[source.cardId]?.name ?? 'Personality power',
    defender: event.player === 0 ? 1 : 0, status: 'incoming' };
}

export function touchControls() {
  return matchMedia('(pointer: coarse)').matches || innerWidth < 600 || (innerWidth <= 1000 && innerHeight <= 600);
}
